import { CheckboxItem } from "../types/export";
import { ExportFormSchemaType } from "../zod-schemas/export-form";

export const InitialClientProfileSectionsOptions = [
  { label: "Demographic", key: "demographic", checked: false },
  { label: "Contact Information", key: "contactInformation", checked: false },
  { label: "Background Information", key: "backgroundInformation", checked: false },
  { label: "Support Network", key: "supportNetwork", checked: false },
];

export const InitialIntakeFormSectionsOptions = [
  { label: "General Health", key: "generalHealth", checked: false },
  { label: "Gynecological & Sexual Health", key: "gynecologicalAndSexualHealth", checked: false },
  { label: "Lifestyle", key: "lifestyle", checked: false },
  { label: "Current Pregnancy", key: "currentPregnancy", checked: false },
  { label: "Closed Pregnancies", key: "closedPregnancy", checked: false },
  { label: "Past Pregnancies", key: "pastPregnancies", checked: false },
  { label: "Family History", key: "familyHistory", checked: false },
];

export const InitialBillingSectionsOptions = [
  {
    label: "Insurance and Billing Information",
    key: "billingInformation",
    checked: false,
  },
];

export const EncounterTypeFlowsheetMap = [
  { code: "LP135482-0", display: "Prenatal", hasFlowsheet: true },
  { code: "96541-8", display: "Postpartum", hasFlowsheet: false },
  { code: "LP32899-4", display: "Gynecological", hasFlowsheet: false },
  { code: "LP281763-5", display: "Newborn IPP", hasFlowsheet: false },
  { code: "LP20597-8", display: "Newborn Postpartum", hasFlowsheet: true },
  { code: "57083-8", display: "Labor and Delivery", hasFlowsheet: true },
  { code: "78624-4", display: "Primary Care", hasFlowsheet: false },
  {
    code: "forms/encounters/category/transfer-of-care",
    display: "Transfer of Care",
    hasFlowsheet: false,
  },
];

export const ProblemsAllergiesMedicationsSectionKeys = {
  PROBLEMS: "problems",
  ALLERGIES: "allergies",
  MEDICATIONS: "medications",
} as const;

export const InitialProblemsAllergiesMedicationsSectionsOptions = [
  { label: "Problems", key: ProblemsAllergiesMedicationsSectionKeys.PROBLEMS, checked: false },
  { label: "Allergies", key: ProblemsAllergiesMedicationsSectionKeys.ALLERGIES, checked: false },
  {
    label: "Medications",
    key: ProblemsAllergiesMedicationsSectionKeys.MEDICATIONS,
    checked: false,
  },
];

export const ChartExportSectionKeys = {
  CLIENT_PROFILE: "clientProfile",
  BILLING: "billing",
  PROBLEMS_ALLERGIES_MEDICATIONS: "problemsAllergiesMedications",
  INTAKE_FORM: "intakeForm",
} as const;

export type ChartExportSectionKey =
  (typeof ChartExportSectionKeys)[keyof typeof ChartExportSectionKeys];

export type ChartExportSection = {
  key: ChartExportSectionKey;
  /** Heading shown on both the selection drawer and the exported PDF. */
  title: string;
  options: CheckboxItem[];
  /** Export form field carrying this section's selected option keys through to the viewer. */
  formField: keyof ExportFormSchemaType;
  showSelectAll: boolean;
};

/**
 * Canonical order of the checkbox-driven chart export sections. The selection drawer and the
 * exported PDF both derive their order from this array, so insurance and billing stay grouped
 * with the demographic sections wherever the sections are rendered.
 */
export const CHART_EXPORT_SECTIONS: ChartExportSection[] = [
  {
    key: ChartExportSectionKeys.CLIENT_PROFILE,
    title: "Client Profile",
    options: InitialClientProfileSectionsOptions,
    formField: "clientProfileSections",
    showSelectAll: true,
  },
  {
    key: ChartExportSectionKeys.BILLING,
    title: "Billing",
    options: InitialBillingSectionsOptions,
    formField: "billingSections",
    // Billing offers a single option, so a "select all" control would duplicate it.
    showSelectAll: false,
  },
  {
    key: ChartExportSectionKeys.PROBLEMS_ALLERGIES_MEDICATIONS,
    title: "Problems, Allergies & Medications",
    options: InitialProblemsAllergiesMedicationsSectionsOptions,
    formField: "problemsAllergiesMedicationsSections",
    showSelectAll: true,
  },
  {
    key: ChartExportSectionKeys.INTAKE_FORM,
    title: "Intake Form",
    options: InitialIntakeFormSectionsOptions,
    formField: "intakeFormSections",
    showSelectAll: true,
  },
];
